import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CRITICAL_HOURS_PER_WEEK,
  MIN_HOURS_PER_WEEK,
  MIN_TOTAL_STUDY_HOURS,
  MIN_WEEKS,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  BoosterTargets,
  InsufficientScheduleReason,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { estimatePlanCoverage, PlanCoverage } from './coverageEstimate';

const STRINGS = appStrings.modules.satActBooster.insufficientSchedule;

/**
 * The insufficient-schedule reasons a draft's targets violate, computed locally so the schedule step
 * reacts to edits before anything is sent. Reason codes and their order match the server's.
 *
 * Nothing is assessed until the start-to-test window and a study day yield a coverage estimate:
 * total hours can't be measured without one, and an empty schedule is a required-field error
 * rather than a light-schedule warning.
 */
export const getLocalInsufficientReasons = (
  targets: BoosterTargets,
  coverage: PlanCoverage | null = estimatePlanCoverage(targets),
): InsufficientScheduleReason[] => {
  if (!coverage) return [];

  const hoursPerWeek = targets.hoursPerDay * targets.studyDays.length;
  const criticallyLowRate = hoursPerWeek < CRITICAL_HOURS_PER_WEEK;
  const thinWeeklyRate = hoursPerWeek < MIN_HOURS_PER_WEEK;
  const thinTotalHours = targets.hoursPerDay * coverage.sessions < MIN_TOTAL_STUDY_HOURS;

  const weeklyRateNamed = criticallyLowRate || (thinWeeklyRate && thinTotalHours);

  const reasons: InsufficientScheduleReason[] = [];

  if (criticallyLowRate) reasons.push('hours_per_week_critically_low');
  else if (thinWeeklyRate && thinTotalHours) reasons.push('hours_per_week_below_minimum');
  if (coverage.weeks < MIN_WEEKS) reasons.push('fewer_than_minimum_weeks');
  if (thinTotalHours && !weeklyRateNamed) reasons.push('total_hours_below_minimum');

  return reasons;
};

/** Copy for a reason code; an unrecognized code falls back to a generic line rather than crashing. */
export const insufficientReasonCopy = (reason: string): string => {
  switch (reason) {
    case 'hours_per_week_below_minimum':
      return STRINGS.hours_per_week_below_minimum;
    case 'hours_per_week_critically_low':
      return STRINGS.hours_per_week_critically_low;
    case 'fewer_than_minimum_weeks':
      return STRINGS.fewer_than_minimum_weeks;
    case 'total_hours_below_minimum':
      return STRINGS.total_hours_below_minimum;
    default:
      return STRINGS.generic;
  }
};
